// AuthProvider hydrates the session on mount (via /me, which the apiClient will
// silently refresh) and exposes login/signup/google/logout. Wrap your app in it
// and read state with useAuth().
import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import type { AxiosInstance, AxiosResponse } from 'axios';
import type { ApiResponse, AuthResult, AuthUser } from '../types.js';
import { useAuthStore, type AuthStatus } from './authStore.js';

interface AuthContextValue {
  user: AuthUser | null;
  status: AuthStatus;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, name?: string) => Promise<void>;
  loginWithGoogle: (idToken: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function unwrap<T>(res: AxiosResponse<ApiResponse<T>>): T {
  if (!res.data.success) throw new Error(res.data.error);
  return res.data.data;
}

export function AuthProvider({ client, children }: { client: AxiosInstance; children: ReactNode }) {
  const { user, status, setAuth, setStatus, clear } = useAuthStore();
  const bootstrapped = useRef(false);

  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;
    setStatus('loading');
    client
      .get<ApiResponse<{ user: AuthUser }>>('/api/auth/me')
      .then((res) => {
        const { user: me } = unwrap(res);
        setAuth(me, useAuthStore.getState().accessToken ?? '');
      })
      .catch(() => clear());
  }, [client, setStatus, setAuth, clear]);

  const complete = (result: AuthResult) => setAuth(result.user, result.accessToken);

  const value: AuthContextValue = {
    user,
    status,
    login: async (email, password) => complete(unwrap(await client.post('/api/auth/login', { email, password }))),
    signup: async (email, password, name) =>
      complete(unwrap(await client.post('/api/auth/signup', { email, password, name }))),
    loginWithGoogle: async (idToken) => complete(unwrap(await client.post('/api/auth/google', { idToken }))),
    logout: async () => {
      await client.post('/api/auth/logout').catch(() => {});
      clear();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
